import queryString from 'query-string';

const VALID_TLDS = [
  `aero`,
  `biz`,
  `cat`,
  `com`,
  `coop`,
  `edu`,
  `gov`,
  `info`,
  `int`,
  `jobs`,
  `mil`,
  `mobi`,
  `museum`,
  `name`,
  `net`,
  `org`,
  `travel`,
  `ac`,
  `ad`,
  `ae`,
  `af`,
  `ag`,
  `ai`,
  `al`,
  `am`,
  `an`,
  `ao`,
  `aq`,
  `ar`,
  `as`,
  `at`,
  `au`,
  `aw`,
  `az`,
  `ba`,
  `bb`,
  `bd`,
  `be`,
  `bf`,
  `bg`,
  `bh`,
  `bi`,
  `bj`,
  `bm`,
  `bn`,
  `bo`,
  `br`,
  `bs`,
  `bt`,
  `bv`,
  `bw`,
  `by`,
  `bz`,
  `ca`,
  `cc`,
  `cd`,
  `cf`,
  `cg`,
  `ch`,
  `ci`,
  `ck`,
  `cl`,
  `cm`,
  `cn`,
  `co`,
  `cr`,
  `cs`,
  `cu`,
  `cv`,
  `cx`,
  `cy`,
  `cz`,
  `de`,
  `dj`,
  `dk`,
  `dm`,
  `do`,
  `dz`,
  `ec`,
  `ee`,
  `eg`,
  `eh`,
  `er`,
  `es`,
  `et`,
  `eu`,
  `fi`,
  `fj`,
  `fk`,
  `fm`,
  `fo`,
  `fr`,
  `ga`,
  `gb`,
  `gd`,
  `ge`,
  `gf`,
  `gg`,
  `gh`,
  `gi`,
  `gl`,
  `gm`,
  `gn`,
  `gp`,
  `gq`,
  `gr`,
  `gs`,
  `gt`,
  `gu`,
  `gw`,
  `gy`,
  `hk`,
  `hm`,
  `hn`,
  `hr`,
  `ht`,
  `hu`,
  `id`,
  `ie`,
  `il`,
  `im`,
  `in`,
  `io`,
  `iq`,
  `ir`,
  `is`,
  `it`,
  `je`,
  `jm`,
  `jo`,
  `jp`,
  `ke`,
  `kg`,
  `kh`,
  `ki`,
  `km`,
  `kn`,
  `kp`,
  `kr`,
  `kw`,
  `ky`,
  `kz`,
  `la`,
  `lb`,
  `lc`,
  `li`,
  `lk`,
  `lr`,
  `ls`,
  `lt`,
  `lu`,
  `lv`,
  `ly`,
  `ma`,
  `mc`,
  `md`,
  `mg`,
  `mh`,
  `mk`,
  `ml`,
  `mm`,
  `mn`,
  `mo`,
  `mp`,
  `mq`,
  `mr`,
  `ms`,
  `mt`,
  `mu`,
  `mv`,
  `mw`,
  `mx`,
  `my`,
  `mz`,
  `na`,
  `nc`,
  `ne`,
  `nf`,
  `ng`,
  `ni`,
  `nl`,
  `no`,
  `np`,
  `nr`,
  `nu`,
  `nz`,
  `om`,
  `pa`,
  `pe`,
  `pf`,
  `pg`,
  `ph`,
  `pk`,
  `pl`,
  `pm`,
  `pn`,
  `pr`,
  `ps`,
  `pt`,
  `pw`,
  `py`,
  `qa`,
  `re`,
  `ro`,
  `ru`,
  `rw`,
  `sa`,
  `sb`,
  `sc`,
  `sd`,
  `se`,
  `sg`,
  `sh`,
  `si`,
  `sj`,
  `sk`,
  `sl`,
  `sm`,
  `sn`,
  `so`,
  `sr`,
  `st`,
  `su`,
  `sv`,
  `sy`,
  `sz`,
  `tc`,
  `td`,
  `tf`,
  `tg`,
  `th`,
  `tj`,
  `tk`,
  `tm`,
  `tn`,
  `to`,
  `tp`,
  `tr`,
  `tt`,
  `tv`,
  `tw`,
  `tz`,
  `ua`,
  `ug`,
  `uk`,
  `um`,
  `us`,
  `uy`,
  `uz`,
  `va`,
  `vc`,
  `ve`,
  `vg`,
  `vi`,
  `vn`,
  `vu`,
  `wf`,
  `ws`,
  `ye`,
  `yt`,
  `yu`,
  `za`,
  `zm`,
  `zr`,
  `zw`,
];

const TLD_REGEX = new RegExp(`\\.(${VALID_TLDS.join(`|`)})\\b`);
const HTTP_PROTOCOL_REGEX = /^https?:\/\//;

export function addProtocolIfAbsent(url: string): string {
  if (!HTTP_PROTOCOL_REGEX.test(url)) {
    return `http://` + url;
  }

  return url;
}

export function isURL(str) {
  if (typeof str !== `string` || str.includes(` `)) {
    return false;
  }
  const hasHTTPishProtocol = HTTP_PROTOCOL_REGEX.test(str);
  const hasNonHTTPishProtocol = /^\w+:\/\//.test(str) && !hasHTTPishProtocol;
  const hasFTPProtocol = str.startsWith(`ftp://`);
  const maybeEmail = !hasHTTPishProtocol && !hasFTPProtocol && str.includes(`@`);
  const maybeUrl = hasHTTPishProtocol || TLD_REGEX.test(str);
  return maybeUrl && !maybeEmail && !hasNonHTTPishProtocol;
}

function parseFeatureFlagsOverridesStr(featureFlagsStr) {
  if (!featureFlagsStr) {
    return [];
  }
  return featureFlagsStr.split(`,`);
}

export function getFeatureFlagUrlOverrides() {
  const urlOptions = queryString.parse(window.location.search);
  const enabledFeatureFlags = parseFeatureFlagsOverridesStr(urlOptions[`feature-flags`]);
  const disabledFeatureFlags = parseFeatureFlagsOverridesStr(urlOptions[`disable-feature-flags`]);
  return Object.fromEntries([
    ...enabledFeatureFlags.map((flag) => [flag, true]),
    ...disabledFeatureFlags.map((flag) => [flag, false]),
  ]);
}

export function parseUrl(url: string): {host: string; pathname: string; url: string} {
  let sanitizedUrl = !url ? window.location.href : url;
  if (sanitizedUrl.startsWith(`/`) && !sanitizedUrl.startsWith(`//`)) {
    sanitizedUrl = `${window.location.origin}${sanitizedUrl}`;
  } else if (sanitizedUrl.startsWith(`//`)) {
    sanitizedUrl = `${window.location.protocol}${sanitizedUrl}`;
  }

  const {pathname, host, href} = new URL(sanitizedUrl);
  return {
    host,
    pathname,
    url: href,
  };
}

export function getHost(allowLocalHost = false): string {
  const {host, protocol} = window.location;
  return host !== `localhost` || allowLocalHost ? `${protocol}//${host}` : `https://mixpanel.com`;
}

export function getHashString(): string {
  return window.location.hash.replace(/^#/, ``);
}
